/**
 * Project Documents (.md) — durable store on Firebase.
 * Firestore: projects/{projectId}/documents/{docId} metadata
 * Storage:   projects/{projectId}/documents/{name}.md
 *
 * Local Express disk remains a write-through mirror for scan/seed APIs.
 */
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  setDoc,
} from 'firebase/firestore';
import {
  deleteObject,
  getBytes,
  ref,
  uploadString,
} from 'firebase/storage';
import { auth, db, storage } from '../firebase';
import { writesStayLocal } from './localOnly';

function requireAuth() {
  const user = auth.currentUser;
  if (!user) {
    const err = new Error(
      'Sign in required to save project documents to Firebase (use system Chrome/Safari at http://localhost:3001).'
    );
    err.code = 'AUTH_REQUIRED';
    throw err;
  }
  return user;
}

/**
 * Stable document id/filename for overwrite semantics.
 * Lowercases so "Notes.md" and "notes.md" replace the same Storage object + Firestore doc.
 */
export function safeDocumentName(name) {
  const base = String(name || 'document.md').split(/[/\\]/).pop().trim();
  let cleaned = base.replace(/[^a-zA-Z0-9._\- ]+/g, '-').replace(/\s+/g, '-');
  if (!cleaned.toLowerCase().endsWith('.md')) {
    cleaned = `${cleaned || 'document'}.md`;
  }
  cleaned = cleaned.toLowerCase() || 'document.md';
  // Collapse .markdown → .md for one canonical object per basename.
  if (cleaned.endsWith('.markdown')) {
    cleaned = `${cleaned.slice(0, -'.markdown'.length)}.md`;
  }
  return cleaned;
}

function metaRef(projectId, docId) {
  return doc(db, 'projects', projectId, 'documents', docId);
}

function storageRefFor(projectId, name) {
  return ref(storage, `projects/${projectId}/documents/${name}`);
}

function toMeta(projectId, data, id) {
  return {
    id,
    name: data.name || id,
    projectId,
    path: data.storagePath || `projects/${projectId}/documents/${data.name || id}`,
    storagePath: data.storagePath || `projects/${projectId}/documents/${data.name || id}`,
    size: data.size || 0,
    updatedAt: data.updatedAt || null,
    createdAt: data.createdAt || null,
    contentType: data.contentType || 'text/markdown',
    source: 'firebase',
  };
}

export async function listCloudDocuments(projectId) {
  requireAuth();
  const snap = await getDocs(collection(db, 'projects', projectId, 'documents'));
  const docs = snap.docs.map((d) => toMeta(projectId, d.data(), d.id));
  docs.sort((a, b) => a.name.localeCompare(b.name));
  return docs;
}

export async function getCloudDocument(projectId, documentId) {
  requireAuth();
  const name = safeDocumentName(documentId);
  const metaSnap = await getDoc(metaRef(projectId, name));
  if (!metaSnap.exists()) return null;
  const meta = toMeta(projectId, metaSnap.data(), metaSnap.id);
  const bytes = await getBytes(storageRefFor(projectId, name));
  const markdown = new TextDecoder().decode(bytes);
  return { ...meta, markdown };
}

export async function upsertCloudDocument(projectId, { name, filename, markdown }) {
  if (writesStayLocal()) return { skipped: true, reason: 'LOCAL' };
  requireAuth();
  const text = typeof markdown === 'string' ? markdown : '';
  if (!text.trim()) {
    const err = new Error('markdown content is required');
    err.code = 'VALIDATION';
    throw err;
  }
  const docName = safeDocumentName(name || filename || 'notes.md');
  const storagePath = `projects/${projectId}/documents/${docName}`;
  const now = new Date().toISOString();
  const existing = await getDoc(metaRef(projectId, docName));
  const replaced = existing.exists();
  const createdAt = replaced ? existing.data().createdAt || now : now;

  // Same path always overwrites the Storage object (no duplicate objects).
  await uploadString(storageRefFor(projectId, docName), text, 'raw', {
    contentType: 'text/markdown; charset=utf-8',
    customMetadata: { projectId, name: docName },
  });

  const size = new TextEncoder().encode(text).length;
  await setDoc(
    metaRef(projectId, docName),
    {
      id: docName,
      name: docName,
      projectId,
      storagePath,
      size,
      contentType: 'text/markdown',
      updatedAt: now,
      createdAt,
      updatedBy: auth.currentUser.email || null,
      replacedAt: replaced ? now : null,
    },
    { merge: true }
  );

  return {
    id: docName,
    name: docName,
    projectId,
    path: storagePath,
    storagePath,
    size,
    updatedAt: now,
    createdAt,
    contentType: 'text/markdown',
    source: 'firebase',
    replaced,
    markdown: text,
  };
}

export async function deleteCloudDocument(projectId, documentId) {
  if (writesStayLocal()) return { skipped: true, reason: 'LOCAL' };
  requireAuth();
  const rawId = String(documentId || '').split(/[/\\]/).pop().trim();
  const name = safeDocumentName(documentId);
  // Prefer the listed id (may be Mixed_Case from older uploads), then the
  // canonical lowercase safe name used by upsert.
  let metaSnap = rawId ? await getDoc(metaRef(projectId, rawId)) : null;
  let resolvedName = rawId;
  if (!metaSnap?.exists() && name && name !== rawId) {
    metaSnap = await getDoc(metaRef(projectId, name));
    resolvedName = name;
  }
  if (!metaSnap?.exists()) {
    const err = new Error('Document not found');
    err.code = 'NOT_FOUND';
    throw err;
  }
  const storageName = metaSnap.data()?.name || resolvedName;
  try {
    await deleteObject(storageRefFor(projectId, storageName));
  } catch (e) {
    // Ignore missing storage object; still clear metadata.
    if (e?.code !== 'storage/object-not-found') throw e;
  }
  await deleteDoc(metaRef(projectId, metaSnap.id));
  return { deleted: true, id: metaSnap.id, projectId, source: 'firebase' };
}

/**
 * One-shot: push any local-disk Adventure Awaits (or other) docs that are not yet in Firebase.
 * `fetchLocalList` / `fetchLocalDoc` come from the Express API helpers.
 */
export async function migrateLocalDocumentsToCloud(
  projectId,
  { fetchLocalList, fetchLocalDoc }
) {
  if (writesStayLocal()) return { migrated: 0, skipped: true, reason: 'LOCAL' };
  requireAuth();
  const flagKey = `artificiallly:docs-migrated:${projectId}`;
  if (typeof window !== 'undefined' && window.localStorage.getItem(flagKey) === '1') {
    return { migrated: 0, skipped: true };
  }

  const cloud = await listCloudDocuments(projectId);
  const cloudIds = new Set(cloud.map((d) => d.id));
  let localDocs = [];
  try {
    const data = await fetchLocalList(projectId);
    localDocs = data.documents || [];
  } catch {
    localDocs = [];
  }

  let migrated = 0;
  for (const item of localDocs) {
    if (cloudIds.has(item.id)) continue;
    try {
      const full = await fetchLocalDoc(projectId, item.id);
      const markdown = full?.document?.markdown ?? full?.markdown;
      if (!markdown) continue;
      await upsertCloudDocument(projectId, { name: item.name || item.id, markdown });
      migrated += 1;
    } catch {
      // continue remaining files
    }
  }

  if (typeof window !== 'undefined') {
    window.localStorage.setItem(flagKey, '1');
  }
  return { migrated, skipped: false };
}

/**
 * Push cloud documents down to local disk so Express scan/seed still work.
 * Skips files that already match local size to avoid re-downloading ~200KB+
 * bible packs on every Scan.
 */
export async function mirrorCloudDocumentsToLocal(
  projectId,
  { upsertLocal, fetchLocalList }
) {
  requireAuth();
  const cloud = await listCloudDocuments(projectId);
  const localById = new Map();
  if (typeof fetchLocalList === 'function') {
    try {
      const local = await fetchLocalList(projectId);
      for (const item of local?.documents || []) {
        localById.set(String(item.id || '').toLowerCase(), item);
        if (item.name) localById.set(String(item.name).toLowerCase(), item);
      }
    } catch {
      /* local API may be down — fall through to full mirror */
    }
  }

  const needsMirror = cloud.filter((meta) => {
    const key = String(meta.id || meta.name || '').toLowerCase();
    const local = localById.get(key);
    if (!local) return true;
    if (meta.size != null && local.size != null && Number(meta.size) === Number(local.size)) {
      return false;
    }
    return true;
  });

  let mirrored = 0;
  const concurrency = 4;
  for (let i = 0; i < needsMirror.length; i += concurrency) {
    const batch = needsMirror.slice(i, i + concurrency);
    const results = await Promise.all(
      batch.map(async (meta) => {
        try {
          const full = await getCloudDocument(projectId, meta.id);
          if (!full?.markdown) return 0;
          await upsertLocal(projectId, { name: full.name, markdown: full.markdown });
          return 1;
        } catch {
          return 0;
        }
      })
    );
    mirrored += results.reduce((sum, n) => sum + n, 0);
  }
  return { mirrored, skipped: cloud.length - needsMirror.length, total: cloud.length };
}
